import _ from 'lodash';
// @mui
import { Stack } from '@mui/system';
import { Box, Button, Typography, useTheme } from '@mui/material';
//components
import FormCreator from 'components/FormCreator';
import { fieldType, invalidFields, fieldsGroupType } from 'components/FormCreator/types';
//reducers
import { getLangAsync, selectImageUrl, selectTranslations } from 'state/reducers/translation.reducers';
import { getNewPay, setPay } from 'state/reducers/pay.reducers';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
//sections
import { FormCard } from 'theme/elements/Forms';
import { FormHeader1, HeaderPage4 } from 'theme/elements/Headers';
//constants
import { PayEnum, Pay, paymentReason, MAX_INVOICE_FILES } from 'constants/pay.constants';
import { FormButton } from 'theme/elements/Buttons';
import { CheckCircleOutlineRounded, CloseRounded, UploadFile } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import ErrorBox from 'components/ErrorBox';
import { maxFileSize, acceptableFileFormats } from 'constants/global.constants';
import { validateFileExtensions } from 'utils';
import { getCurrencies } from 'state/reducers/global.reducers';
import CloseIcon from '@mui/icons-material/Close';
import { SinglePaymentCategoryPicker } from 'components/PaymentCategoryPicker';
import { PaymentCategoryOption } from 'constants/beneficiary.constants';
import { normalizePaymentCategories } from 'utils/paymentCategoryUtils';

type formProps = {
	handleChange: any;
	invalidFields: invalidFields;
	currency: any;
	isFileUploaded: boolean;
	handleTransferProofFile: any;
	removeFile: any;
	fileUpload?: boolean;
};
export default function PaymentReasonForm(props: formProps) {
	const t = useAppSelector(selectTranslations);
	const dispatch = useAppDispatch();
	const imageUrl = useAppSelector(selectImageUrl);
	const pay: Pay = useAppSelector(getNewPay);
	const prefixFields = 'page.pay.create.step.2.beneficiary_payment_reason.';
	const theme = useTheme();
	const [showErrorBox, setShowErrorBox] = useState(false);
	const [errorText, setErrorText] = useState<string | null>(null);
	const purposeCodes = useAppSelector((state) => state.global.paymentPurpose);
	const currencies = useAppSelector(getCurrencies);

	const options = useMemo(() => {
		return normalizePaymentCategories(pay.beneficiary?.paymentCategories);
	}, [pay.beneficiary?.paymentCategories]);

	const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		setShowErrorBox(false);
		if (files) {
			const currentFiles = _.get(pay, PayEnum.invoiceFile) || [];
			const remainingSlots = MAX_INVOICE_FILES - currentFiles.length;

			if (files.length > remainingSlots) {
				setErrorText(t(`errors.files.upload.upload_limit_exceeded.title`));
				setShowErrorBox(true);
				event.target.value = '';
				return;
			}

			const validFiles: File[] = [];
			for (let i = 0; i < files.length; i++) {
				const file = files[i];
				if (validateFileExtensions(file.type)) {
					if (file.size <= maxFileSize) {
						validFiles.push(file);
					} else {
						setErrorText(t(`servers.errors.codes.1302`));
						setShowErrorBox(true);
						break;
					}
				} else {
					setErrorText(t(`errors.files.upload.file_type_not_supported.title`));
					setShowErrorBox(true);
					break;
				}
			}
			if (validFiles.length > 0) {
				props.handleTransferProofFile(validFiles);
			}
		}
	};

	return (
		<Box component="form" noValidate>
			<FormCard>
				<FormHeader1 title={t(`${prefixFields}title`)} text={t(`${prefixFields}text`)} />
				<Stack spacing={2}>
					<SinglePaymentCategoryPicker
						value={pay[PayEnum.paymentCategory] || ''}
						onChange={(selected) => {
							dispatch(setPay({ name: PayEnum.paymentCategory, value: selected }));
						}}
						error={Boolean(props.invalidFields?.[PayEnum.paymentCategory])}
						helperText={
							props.invalidFields?.[PayEnum.paymentCategory]
								? t('validations.errors.requierd')
								: undefined
						}
						disabled={false}
						label={t('page.pay.create.step.2.beneficiary_payment_reason.payment_category.label')}
						hideTitle
						required={true}
						options={options}
					/>
					<FormCreator
						initialValidation={props.invalidFields}
						name="localPayment"
						handleChange={props.handleChange}
						fieldsGroups={[
							{
								type: fieldsGroupType.col,
								fields: [
									{
										type: fieldType.text,
										props: {
											name: PayEnum.comment,
											label: t(`${prefixFields}comment.text`),
											value: (pay?.[PayEnum.comment] ?? '').slice(0, 49),
										},
									},
									{
										type: fieldType.text,
										props: {
											name: PayEnum.reference,
											label: t(`${prefixFields}${_.snakeCase(PayEnum.reference)}.text`),
											value: pay[PayEnum.reference] || '',
										},
									},
									{
										type: fieldType.text,
										props: {
											required: true,
											name: PayEnum.invoiceNumber,
											label: t(`${prefixFields}invoice_number.text`),
											value: pay[PayEnum.invoiceNumber] || '',
										},
									},
								],
							},
						]}
					/>
					<Stack
						marginTop="32px"
						sx={{
							backgroundColor: theme.palette.root.brandSecondary_012,
							padding: '32px',
							borderRadius: '8px',
							gap: 3,
						}}
					>
						<>*{t(`${prefixFields}invoice.file_upload_comment.title`)}</>
						<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{}}>
							<HeaderPage4
								title={props.isFileUploaded ? '' : t(`${prefixFields}invoice.file_not_selected.title`)}
								text={t(`${prefixFields}invoice.file_not_selected.text`)}
							/>
							<FormButton>
								<Button
									variant="outlined"
									sx={{
										'&.MuiButton-outlined': {
											borderColor: theme.palette.root.basicDeepGreen,
											maxWidth: '100%',
										},
									}}
									component="label"
									disabled={
										(_.isArray(pay.invoiceFile) && pay.invoiceFile.length >= MAX_INVOICE_FILES) ||
										props.fileUpload
									}
									startIcon={
										props.isFileUploaded
											? imageUrl(prefixFields + 'file_upload_button.success.icon') || (
													<CheckCircleOutlineRounded />
											  )
											: imageUrl(prefixFields + 'file_upload_button.upload.icon') || (
													<UploadFile />
											  )
									}
								>
									{t(`${prefixFields}invoice.file_upload_button.text`)}
									<input
										type="file"
										onChange={handleFileUpload}
										hidden
										accept={acceptableFileFormats}
										multiple={true}
									/>
								</Button>
							</FormButton>
						</Stack>
						<Stack gap={1}>
							{_.isArray(pay.invoiceFile) &&
								pay.invoiceFile.map((file, index) => (
									<Stack key={index} flexDirection={'row'} gap={1}>
										<CloseIcon
											onClick={() => {
												props.removeFile(index);
											}}
											sx={{ height: 'inherit', width: '14px', cursor: 'pointer' }}
										/>
										<Typography
											sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
										>
											{decodeURI(_.get(file, 'originalFileName'))}
										</Typography>
									</Stack>
								))}
						</Stack>
					</Stack>
					{showErrorBox && (
						<ErrorBox
							icon={<img src={imageUrl(prefixFields + 'upload.fail.icon') || '/icons/file_error.svg'} />}
							text={errorText}
						/>
					)}
				</Stack>
			</FormCard>
		</Box>
	);
}
